// v2.19.22f-2 — Onglet Favoris : liste + suppression. Anonyme → CTA login.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { apiFetch } from "@/utils/apiClient";
import { useAuth } from "@/hooks/useAuth";

const API = process.env.REACT_APP_BACKEND_URL || "";

const TYPE_EMOJI = {
  personality: "🌟",
  commerce: "🏪",
  health: "⚕",
  justice: "⚖",
  finance: "💰",
  administration: "🏛",
  shopping: "🛍",
  event: "🎉",
  partner: "🤝",
  sponsored: "📢",
};

export default function FavoritesTab({ onLoginRequest }) {
  const { isAuthenticated } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const r = await apiFetch(`${API}/api/favorites`);
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(data.error || "Erreur de chargement");
        return;
      }
      setItems(data.favorites || []);
    } catch (e) {
      toast.error("Erreur réseau");
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => { reload(); }, [reload]);

  const handleRemove = async (id) => {
    if (!window.confirm("Retirer ce favori ?")) return;
    try {
      const r = await apiFetch(`${API}/api/favorites/${encodeURIComponent(id)}`, { method: "DELETE" });
      if (r.status === 204) {
        toast.success("Retiré");
        reload();
      } else {
        toast.error(`Erreur ${r.status}`);
      }
    } catch (e) { toast.error("Erreur réseau"); }
  };

  if (!isAuthenticated) {
    return (
      <div className="mc-empty">
        <div className="mc-empty-icon">⭐</div>
        <div className="mc-empty-title">Connecte-toi pour gérer tes favoris</div>
        <div className="mc-empty-sub">Sauvegarde tes commerces, médecins, avocats préférés et retrouve-les en un clic.</div>
        <button className="mc-cta" onClick={onLoginRequest} data-testid="favorites-login-cta">
          Se connecter
        </button>
      </div>
    );
  }

  if (loading) {
    return <div className="mc-loading">Chargement des favoris…</div>;
  }

  if (items.length === 0) {
    return (
      <div className="mc-empty">
        <div className="mc-empty-icon">⭐</div>
        <div className="mc-empty-title">Aucun favori pour l'instant</div>
        <div className="mc-empty-sub">
          Clique sur l'étoile ⭐ d'une fiche dans le chat pour l'ajouter ici.
        </div>
      </div>
    );
  }

  return (
    <div className="mc-fav-grid" data-testid="favorites-grid">
      {items.map((fav) => (
        <div key={fav.id} className="mc-fav-card" data-testid={`fav-${fav.id}`}>
          <div className="mc-fav-emoji">{TYPE_EMOJI[fav.item_type] || "⭐"}</div>
          <div className="mc-fav-body">
            <div className="mc-fav-label">{fav.item_label || fav.item_id}</div>
            <div className="mc-fav-meta">
              <span className="mc-fav-type">{fav.item_type}</span>
              <span className="mc-fav-date">
                {new Date(fav.added_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}
              </span>
            </div>
          </div>
          <button
            className="mc-fav-remove"
            onClick={() => handleRemove(fav.id)}
            title="Retirer des favoris"
            data-testid={`fav-remove-${fav.id}`}
          >
            🗑
          </button>
        </div>
      ))}
    </div>
  );
}
